import path from "node:path";

import { defaultHomeForProvider, sanitizeProjectFolderName } from "./sandbox/provider-env.js";
import type { SandboxProvider } from "./sandbox/types.js";

export type StarterRepoInfo = {
  id: string;
  name: string;
  description: string;
  icon?: string;
  source: "github";
  repoFullName: string;
  cloneUrl: string;
};

type GitHubRepoSummary = {
  name: string;
  full_name: string;
  description: string | null;
  clone_url: string;
};

type StarterKitLike = {
  id: string;
  name: string;
  source?: "github" | "local";
};

export function formatStarterRepoDisplayName(repoName: string, prefix = "fv-"): string {
  return repoName
    .replace(prefix, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function mapGitHubStarterRepo(
  repo: GitHubRepoSummary,
  prefix = "fv-",
): StarterRepoInfo {
  return {
    id: repo.name,
    name: formatStarterRepoDisplayName(repo.name, prefix),
    description: repo.description || `Starter project: ${repo.name}`,
    source: "github",
    repoFullName: repo.full_name,
    cloneUrl: repo.clone_url,
  };
}

export function toStarterRepos(
  repos: GitHubRepoSummary[],
  prefix = "fv-",
): StarterRepoInfo[] {
  return repos
    .filter((repo) => repo.name.startsWith(prefix))
    .map((repo) => mapGitHubStarterRepo(repo, prefix));
}

export function filterGithubStarterKits<T extends StarterKitLike>(kits: T[]): T[] {
  return kits.filter((kit) => kit.source === "github");
}

export function pickDefaultStarterKit<T extends StarterKitLike>(kits: T[]): T | null {
  return filterGithubStarterKits(kits)[0] || null;
}

export function computeStarterKitTargetDir(params: {
  provider: SandboxProvider;
  targetFolder?: string;
  repoId: string;
}): string {
  const homeDir = defaultHomeForProvider(params.provider);
  const folderName = params.targetFolder
    ? sanitizeProjectFolderName(params.targetFolder)
    : sanitizeProjectFolderName(params.repoId);
  return path.posix.join(homeDir, folderName);
}
